import React from 'react';
import { Collapse, Grid, makeStyles, Theme } from '@material-ui/core';
import ChevronRightExpand from '../ChevronRightExpand';
import BannerListItem, {
  BannerListItemProps,
  IconContainerGrid,
} from './BannerListItem';
import colorPalette from '../../themes/colorPalette';
import Typo from '../Typo';

const { coolGrey } = colorPalette;

const useStyles = makeStyles<Theme, any>({
  container: {
    borderRadius: '12px',
    overflow: 'hidden',
  },
  headerContainer: ({
    isDisplayingFullContent,
    haveHeaderPadding,
    bgcolor,
  }) => {
    let padding = '16px 20px';
    if (isDisplayingFullContent) {
      if (haveHeaderPadding) padding = '24px 20px 16px';
      else {
        padding = '20px';
      }
    }
    return {
      cursor: isDisplayingFullContent ? 'auto' : 'pointer',
      backgroundColor: bgcolor,
      padding,
    };
  },
  collapsedContainer: {
    width: '100%',
    marginTop: ({ haveHeaderPadding }) => (haveHeaderPadding ? '0px' : '-12px'),
  },
  expandedItemContainer: {
    '& > *': {
      padding: ({ collapsedItemsPaddingY }) =>
        `${Math.round(collapsedItemsPaddingY / 2)}px 20px`,
      backgroundColor: ({ bgcolor }) => bgcolor,
      '&:first-child': {
        paddingTop: ({ haveHeaderPadding, collapsedItemsPaddingY }) =>
          haveHeaderPadding ? '8px' : `${collapsedItemsPaddingY - 8}px`,
      },
      '&:last-child': {
        paddingBottom: '20px',
      },
    },
  },
});

type BannerAccordionProps = {
  isExpanded: boolean;
  onToggleExpand: any;
  headerText: React.ReactNode | string;
  collapsedItems: BannerListItemProps[];
  haveHeaderPadding: boolean;
  className?: string;
  bgcolor?: string;
  collapsedItemsPaddingY?: 12 | 8;
  isDisplayingFullContent?: boolean;
};

function BannerAccordion({
  onToggleExpand,
  isExpanded,
  headerText,
  collapsedItems,
  haveHeaderPadding,
  className = '',
  bgcolor = coolGrey[1],
  collapsedItemsPaddingY = 12,
  isDisplayingFullContent = true,
}: BannerAccordionProps) {
  const classes = useStyles({
    isExpanded,
    haveHeaderPadding,
    isDisplayingFullContent,
    bgcolor,
    collapsedItemsPaddingY,
  });
  return (
    <Grid container className={`${classes.container} ${className}`}>
      <Grid
        container
        item
        alignItems="center"
        justifyContent="space-between"
        className={classes.headerContainer}
        onClick={isDisplayingFullContent ? null : onToggleExpand}
        data-testid="chevron-container-test"
      >
        <Grid item>
          {React.isValidElement(headerText) ? (
            headerText
          ) : (
            <Typo variant="bodyMd" fontWeight="medium">
              {headerText}
            </Typo>
          )}
        </Grid>
        {!isDisplayingFullContent && (
          <IconContainerGrid item>
            <ChevronRightExpand color="action" isExpanded={isExpanded} />
          </IconContainerGrid>
        )}
      </Grid>
      <Collapse
        in={isExpanded || isDisplayingFullContent}
        unmountOnExit
        className={classes.collapsedContainer}
        data-testid="chevron-collapsed-container-test"
      >
        <Grid
          container
          item
          className={classes.expandedItemContainer}
          data-testid="collapsed-list-items"
        >
          {collapsedItems.map((item, idx) => (
            <BannerListItem
              key={idx}
              icon={item.icon}
              primaryText={item.primaryText}
              secondaryText={item.secondaryText}
              textLabel={item.textLabel}
              className={item.className}
            />
          ))}
        </Grid>
      </Collapse>
    </Grid>
  );
}

export default BannerAccordion;
